import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  #handleCartAdd = (event) => {
    const { data } = event.detail;
    if (data && data.sections) {
      this.renderContents({ sections: data.sections });
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartUpdate = (event) => {
    // Skip if this event came from the drawer itself
    if (event.detail?.sourceId === 'cart-drawer') return;

    const { data } = event.detail;
    if (data && data.sections) {
      this.renderContents({ sections: data.sections });
    }
  };

  open() {
    this.showDialog();

    // Fetch fresh cart data when opening to ensure drawer is synced
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // Get the section ID and fetch updated section HTML
        const sectionId = document.querySelector('cart-items-component')?.dataset.sectionId;
        if (sectionId) {
          return fetch(`${window.location.pathname}?sections=${sectionId}`)
            .then(response => response.json())
            .then(sections => {
              this.renderContents({ sections });
            });
        }
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  addToCart(variantId) {
    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        this.renderContents(parsedState);

        // Dispatch global cart update event with item count
        this.dispatchEvent(new CartUpdateEvent(parsedState, 'cart-drawer', {
          itemCount: parsedState.item_count,
          source: 'cart-drawer'
        }));
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
      });
  }

  updateQuantity(line, quantity, event) {
    // console.log('updateQuantity called', line, quantity, event);
    const evt = event || window.event;
    let clickedButton = null;
    if (evt && evt.target) {
      clickedButton = evt.target.closest('button');
    }
    // console.log('clickedButton', clickedButton);

    this.enableLoading(line, clickedButton);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        this.renderContents(parsedState);

        // Dispatch global cart update event with item count
        this.dispatchEvent(new CartUpdateEvent(parsedState, 'cart-drawer', {
          itemCount: parsedState.item_count,
          source: 'cart-drawer'
        }));
      })
      .catch((error) => {
        console.error('Error updating cart:', error);
      })
      .finally(() => {
        this.disableLoading(line, clickedButton);
      });
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      console.warn('No sections in cart response');
      return;
    }

    // Capture current progress bar width before updating
    const oldProgressBar = document.querySelector('.cart-drawer__progress-fill');
    const oldWidth = oldProgressBar ? oldProgressBar.style.width : '0%';

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);

      if (!elementToReplace) {
        console.warn('Element not found for section:', section.id);
        return;
      }

      if (parsedState.sections[section.section]) {
        const newHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
        if (newHTML) {
          elementToReplace.innerHTML = newHTML;
        }
      } else {
        console.warn('Section data not found in response:', section.section);
      }
    });

    // Animate progress bar to new width
    const newProgressBar = document.querySelector('.cart-drawer__progress-fill');
    if (newProgressBar) {
      const targetWidth = newProgressBar.style.width;

      // Snap to old width without animation
      newProgressBar.style.transition = 'none';
      newProgressBar.style.width = oldWidth;

      // Force reflow
      void newProgressBar.offsetWidth;

      // Animate to target width
      newProgressBar.style.transition = 'width 0.5s ease-in-out';
      newProgressBar.style.width = targetWidth;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: document.querySelector('cart-items-component').dataset.sectionId,
        selector: '.cart-drawer__inner',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  enableLoading(line, button) {
    const main = document.querySelector('cart-items-component');
    if (main) main.classList.add('cart__items--disabled');
    if (button) button.classList.add('loading');
  }

  disableLoading(line, button) {
    const main = document.querySelector('cart-items-component');
    if (main) main.classList.remove('cart__items--disabled');
    if (button) button.classList.remove('loading');
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
